import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { scopeText } from '@/lib/scope';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { PersonReportCard } from '@/components/common/PersonReportCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { quantileLabel } from '@/lib/quantiles';
import { PersonConselhoPanels } from '@/pages/anatel/conselho/PersonConselhoPanels';
import { fetchAnatelConselheiro } from '@/services/anatel';
import type { CategoryShare, RelatorDecision } from '@/services/types';

/**
 * One PAF relator.
 *
 * The only person-scoped page in this product, because PAF is the only ANATEL
 * table where a named adjudicator and an outcome share a row -- PADO records an
 * office, and the Acordao vote table records attendance.
 *
 * That makes it the page most at risk of being read as a scorecard, so almost
 * everything here is about saying what the numbers are NOT: rates are observed
 * and unadjusted, credits are not fines, and the collegiate panel is a count
 * with no denominator on the page to divide it by.
 */
export function AnatelConselheiroPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const { slug = '' } = useParams();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['anatel', 'paf', 'relator', slug],
    queryFn: () => fetchAnatelConselheiro(slug),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={3} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4 p-6">
        <TrackLinks />
        <ErrorMessage
          message={notFound ? t('anatel:conselheiro.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-4 p-6">
      <TrackLinks />

      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{data.name}</h1>
          <p className="text-sm text-muted-foreground">{t('anatel:conselheiro.subtitle')}</p>
        </div>
        <ExportButton path={`/api/v1/anatel/export/r/${encodeURIComponent(slug)}`} />
      </header>

      <PersonReportCard reports={data.conselho.reports} />

      <ExplainerCard i18nKey="anatel:conselheiro.explainer" />

      {/* A reader can arrive here from a search without passing the PAF
          overview, so the not-a-fine fence travels with the page rather than
          being inherited from the screen above it. */}
      <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>{t('anatel:paf.separateCorpus')}</span>
      </p>

      {/* No AsOfStrip: this payload describes one person and has no span of its
          own beyond their own decisions, which the identity block states. The
          scope line still travels. */}
      <p className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        {/* `scopeText`, not a locale string of its own. The backend already
            sends this sentence and its translation key, and keeping a second
            copy here is how five pages ended up rendering English after the
            shared strip was translated. */}
        {scopeText(data.as_of, t)}
      </p>

      <section className="rounded-lg border bg-card p-4">
        <dl className="grid gap-3 sm:grid-cols-4">
          <Stat label={t('anatel:conselheiro.identity.decisions')} value={number.format(data.decisions_total)} />
          <Stat
            label={t('anatel:conselheiro.identity.classified')}
            value={number.format(data.classified_total)}
          />
          <Stat
            label={t('anatel:conselheiro.identity.span')}
            value={data.earliest && data.latest ? `${data.earliest} — ${data.latest}` : '—'}
          />
          <Stat
            label={t('anatel:conselheiro.identity.credit')}
            value={number.format(data.decisions.value?.filter((d) => d.credit_amount_brl !== null).length ?? 0)}
          />
        </dl>
        <div className="mt-3">
          <FieldGuide i18nKey="anatel:conselheiro.guide.record" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('anatel:conselheiro.mix.title')}
        </h2>
        {/* scope="record" on every Answer here: an absence is about THIS person,
            not about what ANATEL can support. At the default `corpus` the badge
            reads "Indisponivel para esta agencia", which on a relator with three
            decisions tells a lawyer the agency has no relator data at all. */}
        <Answer answer={data.outcome_mix} help={t('anatel:conselheiro.mix.help')} scope="record">
          {(shares) => <ShareList shares={shares} />}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('anatel:conselheiro.comparison.title')}
        </h2>
        <Answer
          answer={data.outcome_mix}
          help={t('anatel:conselheiro.comparison.help')}
          scope="record"
        >
          {(shares) => <Comparison mine={shares} corpus={data.corpus_mix.value ?? []} />}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="anatel:conselheiro.guide.comparison" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('anatel:conselheiro.credit.title')}
        </h2>
        <Answer answer={data.credit} help={t('anatel:conselheiro.credit.help')} scope="record">
          {(summary) => (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-5">
              {Object.entries(summary.quantiles).map(([key, value]) => (
                <div key={key}>
                  <dt className="text-muted-foreground">{quantileLabel(t, key)}</dt>
                  <dd className="tabular-nums text-foreground">
                    {new Intl.NumberFormat(i18n.language, {
                      style: 'currency',
                      currency: 'BRL',
                      maximumFractionDigits: 0,
                    }).format(value)}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-panel="collegiate">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('anatel:conselheiro.collegiate.title')}
        </h2>
        <Answer
          answer={data.collegiate}
          help={t('anatel:conselheiro.collegiate.help')}
          scope="record"
        >
          {(collegiate) => (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">
                {t('anatel:conselheiro.collegiate.count', { count: collegiate.dissents })}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('anatel:conselheiro.collegiate.otherCorpus')}
              </p>
              {collegiate.below_listing_floor ? (
                <p className="text-xs text-muted-foreground">
                  {t('anatel:conselheiro.collegiate.thin')}
                </p>
              ) : null}
              <ul className="space-y-1.5">
                {collegiate.records.map((record, index) => (
                  <li
                    key={`${record.process_number ?? 'sem-numero'}-${index}`}
                    className="rounded border bg-muted/30 p-2 text-xs"
                  >
                    <span className="tabular-nums text-foreground">
                      {record.process_number ?? '—'}
                    </span>
                    {record.reference_date ? (
                      <span className="ml-2 text-muted-foreground">{record.reference_date}</span>
                    ) : null}
                    {record.reasoning_excerpt ? (
                      <p className="mt-1 text-muted-foreground">{record.reasoning_excerpt}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
              <Link
                to="/anatel/acordaos"
                className="inline-block text-xs text-primary underline-offset-4 hover:underline"
              >
                {t('anatel:conselheiro.collegiate.seeAll')}
              </Link>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="anatel:conselheiro.guide.collegiate" />
        </div>
      </section>

      <PersonConselhoPanels conselho={data.conselho} />

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('anatel:conselheiro.decisions.title')}
        </h2>
        <Answer
          answer={data.decisions}
          help={t('anatel:conselheiro.decisions.help')}
          scope="record"
        >
          {(rows) => <DecisionTable rows={rows} />}
        </Answer>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

function ShareList({ shares }: { shares: CategoryShare[] }) {
  const { t, i18n } = useTranslation(['anatel']);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  return (
    <ul className="space-y-1">
      {shares.map((share) => (
        <li key={share.code} className="flex items-baseline justify-between gap-3 text-sm">
          <span className="text-foreground">
            {t(`anatel:paf.outcomes.${share.code}`, { defaultValue: share.code })}
          </span>
          <span className="tabular-nums text-muted-foreground">
            {share.count} · {percent.format(share.share)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * This relator's shares beside the corpus's.
 *
 * Differences are rendered without a direction word -- no "stricter", no "more
 * lenient". The number describes the record; the case mix that would explain it
 * is not in this corpus, and the field guide says so.
 */
function Comparison({ mine, corpus }: { mine: CategoryShare[]; corpus: CategoryShare[] }) {
  const { t, i18n } = useTranslation(['anatel']);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const baseline = new Map(corpus.map((share) => [share.code, share.share]));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th className="py-1 font-medium">{t('anatel:conselheiro.comparison.outcome')}</th>
            <th className="py-1 text-right font-medium">
              {t('anatel:conselheiro.comparison.thisRelator')}
            </th>
            <th className="py-1 text-right font-medium">
              {t('anatel:conselheiro.comparison.corpus')}
            </th>
          </tr>
        </thead>
        <tbody>
          {mine.map((share) => (
            <tr key={share.code} className="border-t">
              <td className="py-1.5 pr-3 text-foreground">
                {t(`anatel:paf.outcomes.${share.code}`, { defaultValue: share.code })}
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-foreground">
                {percent.format(share.share)}
              </td>
              <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                {baseline.has(share.code) ? percent.format(baseline.get(share.code) ?? 0) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DecisionTable({ rows }: { rows: RelatorDecision[] }) {
  const { t, i18n } = useTranslation(['anatel']);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th className="py-1 font-medium">{t('anatel:conselheiro.decisions.process')}</th>
            <th className="py-1 font-medium">{t('anatel:conselheiro.decisions.date')}</th>
            <th className="py-1 font-medium">{t('anatel:conselheiro.decisions.outcomes')}</th>
            <th className="py-1 text-right font-medium">
              {t('anatel:conselheiro.decisions.credit')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.process_key}-${row.decision_number ?? ''}`} className="border-t align-top">
              <td className="py-1.5 pr-3">
                <Link
                  to={`/anatel/paf/c/${row.process_key}`}
                  className="tabular-nums text-primary underline-offset-4 hover:underline"
                >
                  {row.process_number}
                </Link>
              </td>
              <td className="py-1.5 pr-3 tabular-nums text-muted-foreground">
                {row.reference_date ?? '—'}
              </td>
              <td className="py-1.5 pr-3 text-muted-foreground">
                {row.outcome_codes.length === 0 ? (
                  <span className="italic">{t('anatel:conselheiro.decisions.unclassified')}</span>
                ) : (
                  row.outcome_codes
                    .map((code) => t(`anatel:paf.outcomes.${code}`, { defaultValue: code }))
                    .join('; ')
                )}
              </td>
              <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                {row.credit_amount_brl === null ? '—' : currency.format(row.credit_amount_brl)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Two destinations, not one "back".
 *
 * This page is now reached from `/anatel/paf` AND from `/anatel/acordaos`, and
 * a reader can also arrive from a bookmark or a shared link. A single arrow
 * labelled "back" would send three quarters of arrivals somewhere they have
 * never been, which is worse than offering no way out at all -- and deriving the
 * destination from history or `location.state` breaks on the refresh that a
 * shared link always is.
 *
 * So they are labelled as DESTINATIONS rather than as a return.
 */
function TrackLinks() {
  const { t } = useTranslation(['anatel']);
  return (
    <nav className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
      <Link
        to="/anatel/acordaos"
        className="inline-flex items-center gap-1.5 text-primary underline-offset-4 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {t('anatel:conselheiro.toAcordaos')}
      </Link>
      <Link
        to="/anatel/paf"
        className="inline-flex items-center gap-1.5 text-primary underline-offset-4 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {t('anatel:conselheiro.toPaf')}
      </Link>
    </nav>
  );
}
